/**
 * SVG overlay that renders saved measurements + the in-progress draft.
 *
 * Sub-modes:
 *   - linear   : 2 clicks, auto-finish
 *   - polyline : N clicks, double-click / Enter to finish
 *   - area     : N clicks, click first vertex (or double-click) to close
 *   - angle    : exactly 3 clicks ([rayA, vertex, rayB]) then auto-finish
 *   - select   : click to select; drag a vertex to move; Delete removes
 *
 * Coordinates are normalized 0-100 (matches MarkupLayer / AnnotationLayer).
 * Distances/areas are computed in PDF user units, then converted with the
 * page's calibration. If no calibration is set, drawing is locked and the
 * onLockedAttempt callback is invoked so the parent can surface a toast.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PdfMeasurement, PdfMeasurementCalibration, PdfTakeoffLink } from '../../types';
import {
  applyCalibration,
  angleAtVertex,
  formatAngle,
  formatArea,
  formatLength,
  formatPitch,
  normToPdf,
  polygonArea,
  polylineLength,
  segmentLength,
  type DisplayUnit,
} from '../../utils/measurementMath';
import { formatCrossSection, formatTimberOrderLength } from '../../utils/timberTakeoffHelpers';
import './MeasurementLayer.css';

export type MeasurementSubMode = 'linear' | 'polyline' | 'area' | 'angle' | 'select';

interface NormPoint { x: number; y: number }

export interface MeasurementCommitPayload {
  pageNumber: number;
  kind: PdfMeasurement['kind'];
  points: NormPoint[];
}

export interface MeasurementLayerProps {
  pageNumber: number;
  pageNaturalWidth: number;
  pageNaturalHeight: number;
  /** Current on-screen pixel size of the rendered page — used to keep handles and labels constant-size at any zoom. */
  visualWidth: number;
  visualHeight: number;
  measurements: PdfMeasurement[];
  calibration: PdfMeasurementCalibration | null;
  /** What the user is doing right now. */
  subMode: MeasurementSubMode;
  /** Optional explicit display-unit override (otherwise calibration.displayUnit). */
  displayUnitOverride?: DisplayUnit;
  /** When true, angle labels render as roof pitch (X/12) and linear labels append pitch. */
  pitchMode?: boolean;
  enabled: boolean;
  /** Called when user attempts to draw without a calibration; lets the parent surface a toast. */
  onLockedAttempt?: () => void;
  onCommit: (payload: MeasurementCommitPayload) => Promise<void> | void;
  onUpdatePoints: (id: string, points: NormPoint[]) => Promise<void> | void;
  onDelete: (id: string) => Promise<void> | void;
  /** Selection state lives in the layer, but parent may pass an external clear signal via `selectionResetToken`. */
  selectionResetToken?: number;
  /** Measurement IDs that have a timber take-off link. */
  linkedMeasurementIds?: Set<string>;
  /** Take-off item details keyed by measurement ID (shown on the line in take-off mode). */
  takeoffLinksByMeasurementId?: Map<string, PdfTakeoffLink>;
  /** When true, clicking a measurement notifies the parent (take-off edit flow). */
  takeoffMode?: boolean;
  /** Hold Space to click dimension labels and open the take-off editor. */
  spaceHeld?: boolean;
  onMeasurementSelect?: (measurement: PdfMeasurement) => void;
  /** Called on pointerdown before Space+click edit opens — locks PDF scroll position. */
  onTakeoffEditPrepare?: () => void;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function clamp01_100(p: NormPoint): NormPoint {
  return { x: Math.max(0, Math.min(100, p.x)), y: Math.max(0, Math.min(100, p.y)) };
}

function pointsToPath(pts: NormPoint[], close = false): string {
  if (pts.length === 0) return '';
  const parts = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`);
  if (close) parts.push('Z');
  return parts.join(' ');
}

/** Centroid of a polygon for area labels. */
function polygonCentroid(pts: NormPoint[]): NormPoint {
  if (pts.length === 0) return { x: 50, y: 50 };
  let cx = 0, cy = 0;
  for (const p of pts) {
    cx += p.x;
    cy += p.y;
  }
  return { x: cx / pts.length, y: cy / pts.length };
}

const SHAPE_STROKE = '#3b82f6';
const SHAPE_STROKE_SELECTED = '#1d4ed8';
const SHAPE_STROKE_LINKED = '#22c55e';
const SHAPE_STROKE_UNLINKED = '#f59e0b';
const SHAPE_STROKE_W = 0.4; // in 0-100 viewBox units; non-scaling-stroke fixes display width

// Desired on-screen pixel sizes — used inside counter-scaled <g> wrappers so they render crisply at any zoom.
const VERTEX_PX = 5;
const VERTEX_HIT_PX = 14;
const ENDPOINT_DOT_PX = 3;
const LABEL_FONT_PX = 12;
const TAKEOFF_LABEL_FONT_PX = 11;
const LABEL_PAD_X_PX = 9;
const LABEL_PAD_Y_PX = 5;
const TAKEOFF_LABEL_PAD_X_PX = 8;
const TAKEOFF_LABEL_PAD_Y_PX = 4;
const LABEL_OFFSET_PX = 22; // perpendicular offset from the measured line
const ANGLE_ARC_PX = 30;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export function MeasurementLayer({
  pageNumber,
  pageNaturalWidth,
  pageNaturalHeight,
  visualWidth,
  visualHeight,
  measurements,
  calibration,
  subMode,
  displayUnitOverride,
  pitchMode = false,
  enabled,
  onLockedAttempt,
  onCommit,
  onUpdatePoints,
  onDelete,
  selectionResetToken,
  linkedMeasurementIds,
  takeoffLinksByMeasurementId,
  takeoffMode = false,
  spaceHeld = false,
  onMeasurementSelect,
  onTakeoffEditPrepare,
}: MeasurementLayerProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [draft, setDraft] = useState<NormPoint[]>([]);
  const [hover, setHover] = useState<NormPoint | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ measurementId: string; vertexIndex: number } | null>(null);

  const isLocked = !calibration && subMode !== 'select';

  // ---------------- pixel ↔ viewBox helpers ----------------
  // The SVG uses `viewBox="0 0 100 100"` with `preserveAspectRatio="none"`, which
  // stretches viewBox units to (visualWidth/100, visualHeight/100) px independently.
  // We render handles and labels inside <g transform="translate(x,y) scale(sx,sy)">
  // wrappers, where (sx, sy) is the inverse of the viewport stretch. Inside each
  // wrapper, dimensions are in real screen pixels and circles stay perfectly round.
  const safeVw = Math.max(50, visualWidth);
  const safeVh = Math.max(50, visualHeight);
  const sx = 100 / safeVw;
  const sy = 100 / safeVh;
  const pxX = useCallback((px: number) => (px * 100) / safeVw, [safeVw]);
  const pxY = useCallback((px: number) => (px * 100) / safeVh, [safeVh]);

  /** Perpendicular offset (above the line on screen) from a→b, in viewBox units. */
  const perpOffsetAbove = useCallback(
    (a: NormPoint, b: NormPoint, offsetPx: number): { x: number; y: number } => {
      const dxPx = (b.x - a.x) * safeVw / 100;
      const dyPx = (b.y - a.y) * safeVh / 100;
      const len = Math.hypot(dxPx, dyPx);
      if (len < 1e-3) return { x: 0, y: -pxY(offsetPx) };
      const candAx = (-dyPx / len) * offsetPx;
      const candAy = (dxPx / len) * offsetPx;
      const candBx = -candAx;
      const candBy = -candAy;
      const [px, py] = candAy <= candBy ? [candAx, candAy] : [candBx, candBy];
      return { x: (px * 100) / safeVw, y: (py * 100) / safeVh };
    },
    [safeVw, safeVh, pxY],
  );

  const pageMeasurements = useMemo(
    () => measurements.filter(m => m.pageNumber === pageNumber),
    [measurements, pageNumber],
  );

  // Reset selection / draft when the parent or page changes, or the sub-mode changes.
  useEffect(() => {
    setDraft([]);
    setHover(null);
    setDrag(null);
  }, [subMode, pageNumber]);

  useEffect(() => {
    if (subMode !== 'select') setSelectedId(null);
  }, [subMode]);

  useEffect(() => {
    if (selectionResetToken !== undefined) {
      setSelectedId(null);
      setDraft([]);
    }
  }, [selectionResetToken]);

  // Delete-key removal of the selected measurement.
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDraft([]);
        setSelectedId(null);
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        // Don't fight inputs.
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        e.preventDefault();
        const id = selectedId;
        setSelectedId(null);
        void onDelete(id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, selectedId, onDelete]);

  /* ---------------- pointer helpers ---------------- */

  const toNorm = useCallback((e: React.MouseEvent<SVGSVGElement> | React.PointerEvent<SVGSVGElement>): NormPoint => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    return clamp01_100({ x, y });
  }, []);

  const finishAndCommit = useCallback(
    async (kind: PdfMeasurement['kind'], pts: NormPoint[]) => {
      setDraft([]);
      if (pts.length < 2) return;
      await onCommit({ pageNumber, kind, points: pts });
    },
    [onCommit, pageNumber],
  );

  /* ---------------- click / move / dblclick ---------------- */

  const handleMeasurementEditSelect = useCallback(
    (measurementId: string, e: React.MouseEvent | React.PointerEvent) => {
      if (!spaceHeld || !takeoffMode || !onMeasurementSelect) return;
      e.preventDefault();
      e.stopPropagation();
      const measurement = pageMeasurements.find(m => m.id === measurementId);
      if (measurement) onMeasurementSelect(measurement);
    },
    [spaceHeld, takeoffMode, onMeasurementSelect, pageMeasurements],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!enabled) return;
      if (drag) return; // ignore — pointerup handles release
      if (spaceHeld) return;
      if (subMode === 'select') return; // selection is via the shape itself

      if (isLocked) {
        onLockedAttempt?.();
        return;
      }

      const p = toNorm(e);
      if (subMode === 'linear') {
        if (draft.length === 0) {
          setDraft([p]);
          return;
        }
        // Two clicks → auto-commit.
        const pts = [draft[0], p];
        void finishAndCommit('linear', pts);
        return;
      }

      if (subMode === 'angle') {
        const next = [...draft, p];
        if (next.length === 3) {
          void finishAndCommit('angle', next);
          return;
        }
        setDraft(next);
        return;
      }

      if (subMode === 'polyline') {
        setDraft(d => [...d, p]);
        return;
      }

      if (subMode === 'area') {
        // If we have ≥3 points and click near the first vertex → close.
        if (draft.length >= 3) {
          const first = draft[0];
          if (Math.hypot(p.x - first.x, p.y - first.y) < 1.5) {
            void finishAndCommit('area', draft);
            return;
          }
        }
        setDraft(d => [...d, p]);
        return;
      }
    },
    [enabled, drag, spaceHeld, subMode, isLocked, draft, toNorm, finishAndCommit, onLockedAttempt],
  );

  const handleDblClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!enabled) return;
      if (subMode === 'polyline' && draft.length >= 2) {
        e.preventDefault();
        // The dblclick fired right after a click that appended a point.
        // Commit the current draft as-is (don't include the duplicate point).
        void finishAndCommit('polyline', draft);
        return;
      }
      if (subMode === 'area' && draft.length >= 3) {
        e.preventDefault();
        void finishAndCommit('area', draft);
      }
    },
    [enabled, subMode, draft, finishAndCommit],
  );

  const handleMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!enabled) return;
      const p = toNorm(e);
      if (drag) {
        // Update the dragged vertex on the corresponding measurement (optimistic — we don't write yet).
        // We render directly from `dragLivePoints` map.
        setLiveDragPoint(p);
        return;
      }
      if (draft.length > 0 || subMode === 'angle') setHover(p);
    },
    [enabled, toNorm, drag, draft.length, subMode],
  );

  // Live drag preview (separate state so we don't churn the parent during drag).
  const [liveDragPoint, setLiveDragPoint] = useState<NormPoint | null>(null);

  const handlePointerUp = useCallback(
    async (_e: React.PointerEvent<SVGSVGElement>) => {
      if (!drag) return;
      const target = pageMeasurements.find(m => m.id === drag.measurementId);
      if (target && liveDragPoint) {
        const next = target.points.map((p, i) => (i === drag.vertexIndex ? liveDragPoint : p));
        try {
          if (target.id) await onUpdatePoints(target.id, next);
        } catch (err) {
          console.error('Failed to update measurement vertex', err);
        }
      }
      setDrag(null);
      setLiveDragPoint(null);
    },
    [drag, liveDragPoint, pageMeasurements, onUpdatePoints],
  );

  /* ---------------- label rendering helpers ---------------- */

  const formatMeasurement = useCallback(
    (m: PdfMeasurement | { kind: PdfMeasurement['kind']; points: NormPoint[] }): string | null => {
      const pdfPts = m.points.map(p => normToPdf(p, pageNaturalWidth, pageNaturalHeight));
      if (m.kind === 'angle') {
        if (pdfPts.length < 3) return null;
        const deg = angleAtVertex(pdfPts[0], pdfPts[1], pdfPts[2]);
        return pitchMode ? formatPitch(deg) : formatAngle(deg);
      }
      if (!calibration) return '(no scale)';
      if (m.kind === 'linear') {
        if (pdfPts.length < 2) return null;
        const d = segmentLength(pdfPts[0], pdfPts[1]);
        const lengthLabel = formatLength(
          applyCalibration(d, calibration),
          calibration.baseUnit,
          displayUnitOverride || calibration.displayUnit,
        );
        if (pitchMode) {
          // Slope angle from horizontal — meaningful on elevation drawings.
          const dy = pdfPts[1].y - pdfPts[0].y;
          const dx = pdfPts[1].x - pdfPts[0].x;
          const fromHoriz = (Math.atan2(Math.abs(dy), Math.abs(dx)) * 180) / Math.PI;
          return `${lengthLabel} · ${formatPitch(fromHoriz)}`;
        }
        return lengthLabel;
      }
      if (m.kind === 'polyline') {
        if (pdfPts.length < 2) return null;
        const d = polylineLength(pdfPts);
        return formatLength(applyCalibration(d, calibration), calibration.baseUnit, displayUnitOverride || calibration.displayUnit);
      }
      if (m.kind === 'area') {
        if (pdfPts.length < 3) return null;
        return formatArea(polygonArea(pdfPts), calibration);
      }
      return null;
    },
    [calibration, pageNaturalHeight, pageNaturalWidth, displayUnitOverride, pitchMode],
  );

  /**
   * Pick a stable label position per measurement type.
   * `anchor` is where the label *points to* (used by the leader line).
   * `label`  is where the label pill is drawn.
   */
  const labelPlacement = useCallback(
    (m: { kind: PdfMeasurement['kind']; points: NormPoint[] }): { anchor: NormPoint; label: NormPoint; leader: boolean } => {
      if (m.kind === 'linear' && m.points.length >= 2) {
        const [a, b] = m.points;
        const anchor = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const off = perpOffsetAbove(a, b, LABEL_OFFSET_PX);
        return {
          anchor,
          label: { x: anchor.x + off.x, y: anchor.y + off.y },
          leader: true,
        };
      }
      if (m.kind === 'polyline' && m.points.length >= 2) {
        // Find the longest segment, anchor at its midpoint, offset perpendicular.
        let best = { idx: 1, d: -Infinity };
        for (let i = 1; i < m.points.length; i++) {
          const dx = (m.points[i].x - m.points[i - 1].x) * safeVw / 100;
          const dy = (m.points[i].y - m.points[i - 1].y) * safeVh / 100;
          const d = Math.hypot(dx, dy);
          if (d > best.d) best = { idx: i, d };
        }
        const a = m.points[best.idx - 1];
        const b = m.points[best.idx];
        const anchor = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const off = perpOffsetAbove(a, b, LABEL_OFFSET_PX);
        return {
          anchor,
          label: { x: anchor.x + off.x, y: anchor.y + off.y },
          leader: true,
        };
      }
      if (m.kind === 'angle' && m.points.length >= 3) {
        const v = m.points[1];
        // Place above the vertex, no leader needed (the arc already points to the vertex).
        return {
          anchor: v,
          label: { x: v.x, y: Math.max(2, v.y - pxY(LABEL_OFFSET_PX)) },
          leader: false,
        };
      }
      // area
      const c = polygonCentroid(m.points);
      return { anchor: c, label: c, leader: false };
    },
    [perpOffsetAbove, pxY, safeVw, safeVh],
  );

  /** Screen-space angle for text aligned along a segment (keeps labels upright-readable). */
  const segmentLabelAngleDeg = useCallback(
    (a: NormPoint, b: NormPoint): number => {
      const dxPx = (b.x - a.x) * safeVw / 100;
      const dyPx = (b.y - a.y) * safeVh / 100;
      let deg = (Math.atan2(dyPx, dxPx) * 180) / Math.PI;
      if (deg > 90) deg -= 180;
      if (deg < -90) deg += 180;
      return deg;
    },
    [safeVw, safeVh],
  );

  const takeoffLabelPlacement = useCallback(
    (m: { kind: PdfMeasurement['kind']; points: NormPoint[] }): { anchor: NormPoint; angleDeg: number } | null => {
      if (m.kind === 'linear' && m.points.length >= 2) {
        const [a, b] = m.points;
        return {
          anchor: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
          angleDeg: segmentLabelAngleDeg(a, b),
        };
      }
      if (m.kind === 'polyline' && m.points.length >= 2) {
        let best = { idx: 1, d: -Infinity };
        for (let i = 1; i < m.points.length; i++) {
          const dx = (m.points[i].x - m.points[i - 1].x) * safeVw / 100;
          const dy = (m.points[i].y - m.points[i - 1].y) * safeVh / 100;
          const d = Math.hypot(dx, dy);
          if (d > best.d) best = { idx: i, d };
        }
        const a = m.points[best.idx - 1];
        const b = m.points[best.idx];
        return {
          anchor: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
          angleDeg: segmentLabelAngleDeg(a, b),
        };
      }
      return null;
    },
    [segmentLabelAngleDeg, safeVw, safeVh],
  );

  const formatTakeoffPieceLabel = useCallback((link: PdfTakeoffLink): string => {
    const name = link.name?.trim() || 'Piece';
    return link.quantity > 1 ? `${name} × ${link.quantity}` : name;
  }, []);

  /* ---------------- render ---------------- */

  // For drag preview, replace the dragged vertex with the live cursor location.
  const renderPoints = useCallback(
    (m: PdfMeasurement): NormPoint[] => {
      if (!drag || drag.measurementId !== m.id || !liveDragPoint) return m.points;
      return m.points.map((p, i) => (i === drag.vertexIndex ? liveDragPoint : p));
    },
    [drag, liveDragPoint],
  );

  const draftKind: PdfMeasurement['kind'] | null =
    subMode === 'linear' ? 'linear' :
    subMode === 'polyline' ? 'polyline' :
    subMode === 'area' ? 'area' :
    subMode === 'angle' ? 'angle' :
    null;

  // Compose draft + hover ghost.
  const ghostPoints: NormPoint[] | null = (() => {
    if (!enabled || !draftKind || draft.length === 0 || !hover) return null;
    if (draftKind === 'angle') {
      // Show the next ray from the last placed point.
      return [...draft, hover];
    }
    return [...draft, hover];
  })();

  const labelEls = useMemo(() => {
    return pageMeasurements
      .map(m => {
        if (takeoffMode && m.id && takeoffLinksByMeasurementId?.has(m.id)) return null;
        const text = formatMeasurement(m);
        if (!text || !m.id) return null;
        const placement = labelPlacement({ kind: m.kind, points: renderPoints(m) });
        return { id: m.id, text, placement };
      })
      .filter(Boolean) as { id: string; text: string; placement: ReturnType<typeof labelPlacement> }[];
  }, [
    pageMeasurements,
    renderPoints,
    formatMeasurement,
    labelPlacement,
    takeoffMode,
    takeoffLinksByMeasurementId,
  ]);

  const draftLabel = useMemo(() => {
    if (!ghostPoints || !draftKind) return null;
    const text = formatMeasurement({ kind: draftKind, points: ghostPoints });
    if (!text) return null;
    const placement = labelPlacement({ kind: draftKind, points: ghostPoints });
    return { text, placement };
  }, [ghostPoints, draftKind, formatMeasurement, labelPlacement]);

  const takeoffLabelEls = useMemo(() => {
    if (!takeoffMode || !takeoffLinksByMeasurementId?.size) return [];
    return pageMeasurements
      .map(m => {
        if (!m.id) return null;
        const link = takeoffLinksByMeasurementId.get(m.id);
        if (!link) return null;
        const placement = takeoffLabelPlacement({ kind: m.kind, points: renderPoints(m) });
        if (!placement) return null;
        return {
          id: m.id,
          text: formatTakeoffPieceLabel(link),
          crossSectionText: formatCrossSection(link.dimensions) || undefined,
          dimensionText:
            formatTimberOrderLength(link.dimensions) || formatMeasurement(m) || undefined,
          placement,
        };
      })
      .filter(Boolean) as {
      id: string;
      text: string;
      crossSectionText?: string;
      dimensionText?: string;
      placement: { anchor: NormPoint; angleDeg: number };
    }[];
  }, [
    takeoffMode,
    takeoffLinksByMeasurementId,
    pageMeasurements,
    renderPoints,
    takeoffLabelPlacement,
    formatTakeoffPieceLabel,
    formatMeasurement,
  ]);

  const className = [
    'measurement-layer-svg',
    enabled ? 'measurement-layer-svg--active' : '',
    isLocked ? 'measurement-layer-svg--locked' : '',
    subMode === 'select' ? 'measurement-layer-svg--select' : '',
    spaceHeld && takeoffMode ? 'measurement-layer-svg--space-edit' : '',
  ].filter(Boolean).join(' ');

  return (
    <svg
      ref={svgRef}
      className={className}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      onClick={handleClick}
      onDoubleClick={handleDblClick}
      onPointerMove={handleMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={() => { setHover(null); }}
    >
      {/* Saved measurements */}
      {pageMeasurements.map(m => {
        const pts = renderPoints(m);
        const isSelected = m.id === selectedId;
        const isLinked = !!(m.id && linkedMeasurementIds?.has(m.id));
        const stroke = isSelected
          ? SHAPE_STROKE_SELECTED
          : takeoffMode && m.id
            ? isLinked
              ? SHAPE_STROKE_LINKED
              : SHAPE_STROKE_UNLINKED
            : SHAPE_STROKE;
        const onShapeClick = (e: React.MouseEvent) => {
          if (!enabled) return;
          e.stopPropagation();
          if (takeoffMode && spaceHeld && m.id && onMeasurementSelect) {
            onMeasurementSelect(m);
            return;
          }
          if (subMode !== 'select') return;
          if (m.id) setSelectedId(m.id);
        };
        if (m.kind === 'angle') {
          // Two rays + arc + vertex marker.
          const v = pts[1];
          const a = pts[0];
          const b = pts[2];
          const arcRx = pxX(ANGLE_ARC_PX);
          const arcRy = pxY(ANGLE_ARC_PX);
          const angA = Math.atan2(a.y - v.y, a.x - v.x);
          const angB = Math.atan2(b.y - v.y, b.x - v.x);
          const arcStart = { x: v.x + Math.cos(angA) * arcRx, y: v.y + Math.sin(angA) * arcRy };
          const arcEnd = { x: v.x + Math.cos(angB) * arcRx, y: v.y + Math.sin(angB) * arcRy };
          const sweep = ((angB - angA + 2 * Math.PI) % (2 * Math.PI)) > Math.PI ? 0 : 1;
          return (
            <g key={m.id} onClick={onShapeClick}>
              <path
                d={`M ${a.x} ${a.y} L ${v.x} ${v.y} L ${b.x} ${b.y}`}
                className={`measurement-shape ${isSelected ? 'measurement-shape--selected' : ''}`}
                stroke={stroke}
                strokeWidth={SHAPE_STROKE_W}
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={`M ${arcStart.x} ${arcStart.y} A ${arcRx} ${arcRy} 0 ${sweep} 1 ${arcEnd.x} ${arcEnd.y}`}
                className="measurement-shape"
                stroke={stroke}
                strokeWidth={SHAPE_STROKE_W * 0.7}
                vectorEffect="non-scaling-stroke"
              />
              {subMode === 'select' && pts.map((p, i) => (
                <VertexHandle
                  key={i}
                  p={p}
                  sx={sx}
                  sy={sy}
                  visiblePx={VERTEX_PX}
                  hitPx={VERTEX_HIT_PX}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    if (m.id) {
                      setSelectedId(m.id);
                      setDrag({ measurementId: m.id, vertexIndex: i });
                      setLiveDragPoint(p);
                      try { (e.currentTarget as Element).setPointerCapture?.(e.pointerId); } catch { /* noop */ }
                    }
                  }}
                />
              ))}
            </g>
          );
        }
        if (m.kind === 'area') {
          return (
            <g key={m.id} onClick={onShapeClick}>
              <path
                d={pointsToPath(pts, true)}
                className={`measurement-shape-area ${isSelected ? 'measurement-shape--selected' : ''}`}
                stroke={stroke}
                strokeWidth={SHAPE_STROKE_W}
                vectorEffect="non-scaling-stroke"
              />
              {subMode === 'select' && pts.map((p, i) => (
                <VertexHandle
                  key={i}
                  p={p}
                  sx={sx}
                  sy={sy}
                  visiblePx={VERTEX_PX}
                  hitPx={VERTEX_HIT_PX}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    if (m.id) {
                      setSelectedId(m.id);
                      setDrag({ measurementId: m.id, vertexIndex: i });
                      setLiveDragPoint(p);
                      try { (e.currentTarget as Element).setPointerCapture?.(e.pointerId); } catch { /* noop */ }
                    }
                  }}
                />
              ))}
            </g>
          );
        }
        // linear or polyline
        return (
          <g key={m.id} onClick={onShapeClick}>
            <path
              d={pointsToPath(pts)}
              className={`measurement-shape ${isSelected ? 'measurement-shape--selected' : ''}`}
              stroke={stroke}
              strokeWidth={SHAPE_STROKE_W}
              vectorEffect="non-scaling-stroke"
            />
            {/* Linear endpoints get a small dot so the user can see where the measurement begins/ends. */}
            {m.kind === 'linear' && pts.length === 2 && (
              <>
                <g transform={`translate(${pts[0].x},${pts[0].y}) scale(${sx},${sy})`}>
                  <circle r={ENDPOINT_DOT_PX} fill={stroke} />
                </g>
                <g transform={`translate(${pts[1].x},${pts[1].y}) scale(${sx},${sy})`}>
                  <circle r={ENDPOINT_DOT_PX} fill={stroke} />
                </g>
              </>
            )}
            {subMode === 'select' && pts.map((p, i) => (
              <VertexHandle
                key={i}
                p={p}
                sx={sx}
                sy={sy}
                visiblePx={VERTEX_PX}
                hitPx={VERTEX_HIT_PX}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  if (m.id) {
                    setSelectedId(m.id);
                    setDrag({ measurementId: m.id, vertexIndex: i });
                    setLiveDragPoint(p);
                    try { (e.currentTarget as Element).setPointerCapture?.(e.pointerId); } catch { /* noop */ }
                  }
                }}
              />
            ))}
          </g>
        );
      })}

      {/* Saved labels */}
      {labelEls.map(l => (
        <LabelPill
          key={`label-${l.id}`}
          text={l.text}
          anchor={l.placement.anchor}
          label={l.placement.label}
          showLeader={l.placement.leader}
          sx={sx}
          sy={sy}
          editable={spaceHeld && takeoffMode}
          onEditPrepare={onTakeoffEditPrepare}
          onEditClick={e => handleMeasurementEditSelect(l.id, e)}
        />
      ))}

      {takeoffLabelEls.map(l => (
        <TakeoffPieceLabel
          key={`takeoff-${l.id}`}
          text={l.text}
          crossSectionText={l.crossSectionText}
          dimensionText={l.dimensionText}
          anchor={l.placement.anchor}
          angleDeg={l.placement.angleDeg}
          sx={sx}
          sy={sy}
          showDetails={spaceHeld}
          editable={spaceHeld && takeoffMode}
          onEditPrepare={onTakeoffEditPrepare}
          onEditClick={e => handleMeasurementEditSelect(l.id, e)}
        />
      ))}

      {/* In-progress draft (committed vertices) */}
      {draft.map((p, i) => (
        <g key={`d-${i}`} transform={`translate(${p.x},${p.y}) scale(${sx},${sy})`}>
          <circle r={VERTEX_PX} className="measurement-vertex" />
        </g>
      ))}
      {draft.length >= 2 && draftKind === 'polyline' && (
        <path
          d={pointsToPath(draft)}
          className="measurement-shape"
          stroke={SHAPE_STROKE}
          strokeWidth={SHAPE_STROKE_W}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {draft.length >= 2 && draftKind === 'area' && (
        <path
          d={pointsToPath(draft)}
          className="measurement-shape"
          stroke={SHAPE_STROKE}
          strokeWidth={SHAPE_STROKE_W}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {draft.length >= 1 && draftKind === 'angle' && (
        <path
          d={pointsToPath(draft)}
          className="measurement-shape"
          stroke={SHAPE_STROKE}
          strokeWidth={SHAPE_STROKE_W}
          vectorEffect="non-scaling-stroke"
        />
      )}

      {/* Ghost from last vertex to cursor */}
      {ghostPoints && ghostPoints.length >= 2 && (
        <path
          d={pointsToPath(draftKind === 'area'
            ? ghostPoints // open path while drawing area; close on commit
            : ghostPoints)}
          className="measurement-shape measurement-draft-ghost"
          stroke={SHAPE_STROKE}
          strokeWidth={SHAPE_STROKE_W}
          vectorEffect="non-scaling-stroke"
        />
      )}

      {/* Live label for the draft (so the user sees length as they sketch) */}
      {draftLabel && (
        <LabelPill
          text={draftLabel.text}
          anchor={draftLabel.placement.anchor}
          label={draftLabel.placement.label}
          showLeader={draftLabel.placement.leader}
          sx={sx}
          sy={sy}
        />
      )}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function VertexHandle({
  p,
  sx,
  sy,
  visiblePx,
  hitPx,
  onPointerDown,
}: {
  p: NormPoint;
  sx: number;
  sy: number;
  visiblePx: number;
  hitPx: number;
  onPointerDown: (e: React.PointerEvent<SVGCircleElement>) => void;
}) {
  return (
    <g transform={`translate(${p.x},${p.y}) scale(${sx},${sy})`}>
      {/* Visible handle */}
      <circle r={visiblePx} className="measurement-vertex" />
      {/* Larger transparent hit target */}
      <circle
        r={hitPx}
        className="measurement-vertex-hit"
        onPointerDown={onPointerDown}
      />
    </g>
  );
}

/**
 * Label pill rendered inside a counter-scaled <g> so text + radius are exact
 * pixel dimensions and don't get squashed by the SVG viewport's non-uniform stretch.
 *
 * `anchor` = the point on the page the label refers to (line midpoint, vertex, …).
 * `label`  = where the pill is drawn. If they differ and `showLeader` is true,
 *           a thin connector line + small ridge dot at the anchor make the
 *           callout relationship explicit.
 */
function LabelPill({
  text,
  anchor,
  label,
  showLeader,
  sx,
  sy,
  editable = false,
  onEditPrepare,
  onEditClick,
}: {
  text: string;
  anchor: NormPoint;
  label: NormPoint;
  showLeader: boolean;
  sx: number;
  sy: number;
  editable?: boolean;
  onEditPrepare?: () => void;
  onEditClick?: (e: React.MouseEvent | React.PointerEvent) => void;
}) {
  // Pixel-space pill geometry.
  const fontSize = LABEL_FONT_PX;
  const padX = LABEL_PAD_X_PX;
  const padY = LABEL_PAD_Y_PX;
  const charW = fontSize * 0.6;
  const boxW = Math.max(charW * text.length + padX * 2, 32);
  const boxH = fontSize + padY * 2;

  return (
    <g pointerEvents={editable ? 'auto' : 'none'}>
      {showLeader && (
        <>
          <line
            x1={anchor.x}
            y1={anchor.y}
            x2={label.x}
            y2={label.y}
            className="measurement-leader"
            vectorEffect="non-scaling-stroke"
          />
          {/* Tiny anchor dot */}
          <g transform={`translate(${anchor.x},${anchor.y}) scale(${sx},${sy})`}>
            <circle r={2.5} className="measurement-leader-dot" />
          </g>
        </>
      )}
      <g
        transform={`translate(${label.x},${label.y}) scale(${sx},${sy})`}
        pointerEvents={editable ? 'auto' : 'none'}
        className={editable ? 'measurement-label-pill-hit' : undefined}
        onPointerDown={editable ? e => {
          e.preventDefault();
          e.stopPropagation();
          onEditPrepare?.();
        } : undefined}
        onPointerUp={editable ? e => { e.preventDefault(); e.stopPropagation(); onEditClick?.(e); } : undefined}
      >
        <rect
          x={-boxW / 2}
          y={-boxH / 2}
          width={boxW}
          height={boxH}
          rx={boxH / 2}
          ry={boxH / 2}
          className={`measurement-label-pill${editable ? ' measurement-label-pill--editable' : ''}`}
        />
        <text
          x={0}
          y={0}
          dominantBaseline="central"
          textAnchor="middle"
          fontSize={fontSize}
          className="measurement-label-text"
        >
          {text}
        </text>
      </g>
    </g>
  );
}

/**
 * Take-off piece label centered on the measurement line, rotated to follow the segment.
 * Uses counter-scaling so text stays crisp at any zoom level.
 */
function TakeoffPieceLabel({
  text,
  crossSectionText,
  dimensionText,
  anchor,
  angleDeg,
  sx,
  sy,
  showDetails = false,
  editable = false,
  onEditPrepare,
  onEditClick,
}: {
  text: string;
  crossSectionText?: string;
  dimensionText?: string;
  anchor: NormPoint;
  angleDeg: number;
  sx: number;
  sy: number;
  showDetails?: boolean;
  editable?: boolean;
  onEditPrepare?: () => void;
  onEditClick?: (e: React.MouseEvent | React.PointerEvent) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const fontSize = TAKEOFF_LABEL_FONT_PX;
  const dimFontSize = 10;
  const padX = TAKEOFF_LABEL_PAD_X_PX;
  const padY = TAKEOFF_LABEL_PAD_Y_PX;
  const lineGap = 2;
  const hasDetailInfo = !!(crossSectionText || dimensionText);
  const pointerInteractive = editable || hasDetailInfo;
  const revealDetails = showDetails || hovered;

  const detailLines = useMemo(() => {
    if (!revealDetails) return [];
    const lines: { text: string; kind: 'cross' | 'length' }[] = [];
    if (crossSectionText) lines.push({ text: crossSectionText, kind: 'cross' });
    if (dimensionText) lines.push({ text: dimensionText, kind: 'length' });
    return lines;
  }, [revealDetails, crossSectionText, dimensionText]);

  const charWFor = (size: number) => size * (size === fontSize ? 0.58 : 0.55);
  const primaryW = charWFor(fontSize) * text.length + padX * 2;
  const detailWs = detailLines.map(l => charWFor(dimFontSize) * l.text.length + padX * 2);
  const boxW = Math.max(primaryW, ...detailWs, 28);

  const detailBlockH =
    detailLines.length > 0
      ? detailLines.length * dimFontSize + (detailLines.length - 1) * lineGap
      : 0;
  const contentH = fontSize + (detailBlockH > 0 ? lineGap + detailBlockH : 0);
  const boxH = contentH + padY * 2;

  const primaryY = detailLines.length > 0 ? -contentH / 2 + fontSize / 2 : 0;
  const detailYs = detailLines.map((_, i) => {
    const top = -contentH / 2 + fontSize + lineGap;
    return top + i * (dimFontSize + lineGap) + dimFontSize / 2;
  });

  return (
    <g
      transform={`translate(${anchor.x},${anchor.y}) scale(${sx},${sy}) rotate(${angleDeg})`}
      pointerEvents={pointerInteractive ? 'auto' : 'none'}
      className={editable ? 'measurement-takeoff-label-hit' : hasDetailInfo ? 'measurement-takeoff-label-hover' : undefined}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onPointerDown={editable ? e => {
        e.preventDefault();
        e.stopPropagation();
        onEditPrepare?.();
      } : undefined}
      onPointerUp={editable ? e => { e.preventDefault(); e.stopPropagation(); onEditClick?.(e); } : undefined}
      style={{
        cursor: editable ? 'pointer' : hasDetailInfo ? 'help' : 'default',
      }}
    >
      <rect
        x={-boxW / 2}
        y={-boxH / 2}
        width={boxW}
        height={boxH}
        rx={Math.min(boxH / 2, 10)}
        ry={Math.min(boxH / 2, 10)}
        className={`measurement-takeoff-label-pill${editable ? ' measurement-takeoff-label-pill--editable' : ''}`}
      />
      <text
        x={0}
        y={primaryY}
        dominantBaseline="central"
        textAnchor="middle"
        fontSize={fontSize}
        className="measurement-takeoff-label-text"
      >
        {text}
      </text>
      {detailLines.map((line, i) => (
        <text
          key={line.kind}
          x={0}
          y={detailYs[i]}
          dominantBaseline="central"
          textAnchor="middle"
          fontSize={dimFontSize}
          className={
            line.kind === 'cross'
              ? 'measurement-takeoff-label-cross'
              : 'measurement-takeoff-label-dim'
          }
        >
          {line.text}
        </text>
      ))}
    </g>
  );
}
